import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import { useUpdatePropertyLogo } from '~/hooks/apis/accommodation/useUpdatePropertyLogo';

import LogoEditorDialog from './LogoEditorDialog';
import VideoEditorDialog from './VideoEditorDialog';

interface Props {
  propertyId?: string;
  propertyLogoId?: string;
  videoId?: string;
  onSaveVideo: (videoId: string) => void;
}

function renderSupportingAssetHeader(title: string, actionLabel: string, onClick?: () => void) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={2}
      sx={{ px: 3, py: 2.5 }}
    >
      <Typography variant="h5">{title}</Typography>
      <Button variant="outlined" startIcon={<UploadFileIcon />} disabled={!onClick} onClick={onClick}>
        {actionLabel}
      </Button>
    </Stack>
  );
}

export default function LogoVideoSection({ propertyId, propertyLogoId, videoId, onSaveVideo }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [isLogoDialogOpen, setIsLogoDialogOpen] = useState(false);
  const updatePropertyLogoMutation = useUpdatePropertyLogo();
  const videoLabel = videoId ? `Vimeo video ${videoId}` : 'No video uploaded';

  const handleOpenLogoDialog = () => {
    setIsLogoDialogOpen(true);
  };

  const handleCloseLogoDialog = () => {
    setIsLogoDialogOpen(false);
  };

  const handleOpenVideoDialog = () => {
    setIsVideoDialogOpen(true);
  };

  const handleCloseVideoDialog = () => {
    setIsVideoDialogOpen(false);
  };

  const handleSaveLogo = async (logoId: string | null) => {
    if (!propertyId) {
      return;
    }

    await updatePropertyLogoMutation.mutateAsync({ propertyId, logoId });
    enqueueSnackbar('Property logo updated', { variant: 'success' });
    setIsLogoDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
          gap: 3,
          mt: 4,
        }}
      >
        <Paper variant="outlined" sx={supportingAssetCardSx}>
          {renderSupportingAssetHeader('Property logo', 'Replace logo', propertyId ? handleOpenLogoDialog : undefined)}
          <Divider />
          <Stack spacing={2} sx={{ p: 3 }}>
            <Box
              sx={{
                ...supportingAssetMediaSx,
                display: 'grid',
                placeItems: 'center',
                border: 1,
                borderStyle: 'dashed',
                borderColor: 'grey.300',
                borderRadius: 1,
                bgcolor: 'background.default',
                p: 3,
              }}
            >
              {propertyLogoId ? (
                <Image
                  publicId={propertyLogoId}
                  alt="Property logo"
                  options={{ width: 420 }}
                  style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }}
                />
              ) : (
                <Stack alignItems="center" spacing={1} color="text.secondary">
                  <ImageOutlinedIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                  <Typography variant="body1">No logo uploaded</Typography>
                </Stack>
              )}
            </Box>
            <Typography variant="body1" color="text.secondary">
              Shown on Property information sidebar.
            </Typography>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={supportingAssetCardSx}>
          {renderSupportingAssetHeader('Video', 'Replace video', handleOpenVideoDialog)}
          <Divider />
          <Stack spacing={2} sx={{ p: 3 }}>
            <Box
              sx={{
                ...supportingAssetMediaSx,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
                bgcolor: 'grey.900',
              }}
            >
              {videoId ? (
                <Box
                  component="iframe"
                  title={videoLabel}
                  src={`https://player.vimeo.com/video/${videoId}`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                    display: 'block',
                  }}
                />
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    color: 'grey.400',
                  }}
                >
                  <VideoCallOutlinedIcon fontSize="large" />
                </Stack>
              )}
            </Box>
            <Typography variant="body1" color="text.secondary">
              Appended to carousel hero #2 and shown on each room gallery.
            </Typography>
          </Stack>
        </Paper>
      </Box>

      <LogoEditorDialog
        open={isLogoDialogOpen}
        logoId={propertyLogoId}
        isSaving={updatePropertyLogoMutation.isPending}
        onClose={handleCloseLogoDialog}
        onSave={handleSaveLogo}
      />
      <VideoEditorDialog
        open={isVideoDialogOpen}
        videoId={videoId}
        onClose={handleCloseVideoDialog}
        onSave={onSaveVideo}
      />
    </>
  );
}

const supportingAssetCardSx = {
  overflow: 'hidden',
  minWidth: 0,
};

const supportingAssetMediaSx = {
  aspectRatio: '16 / 9',
  width: '100%',
};
